import { FC } from "react";
import { useNavigate } from "react-router-dom";

import { Input } from "@/shared/ui";
import { Tarif } from "@/widgets/subscription-slider/model/types";
import { baseTarifs } from "@/widgets/subscription-slider/model/constants";

import styles from "./PurchaseCard.module.scss";
import IconDone from "@/assets/done.png";
import { cn } from "@/shared/utils";
import { useForm, SubmitHandler } from "react-hook-form";
import { useCreatePaymentMutation } from "@/store/paymentApi";
import { useYandexMetrika, GOALS } from "@/hooks/useYandexMetrika";

type PurchaseCardProps = {
  tarif: Tarif;
};

type FormValues = {
  email: string;
};

export const PurchaseCard: FC<PurchaseCardProps> = ({ tarif }) => {
  const navigate = useNavigate();
  const { handleSubmit, control } = useForm<FormValues>();
  const [createPayment] = useCreatePaymentMutation();
  const { trackAction } = useYandexMetrika();

  if (!tarif) return null;
  const isFreeTariff = !tarif.is_trial && tarif.cost === 0;

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      trackAction(GOALS.ACTION_EMAIL_ENTERED, {
        email: data.email,
        tarifId: tarif.id,
        isTrial: tarif.is_trial
      });

      const response = await createPayment({
        service_id: tarif.id,
      }).unwrap();

      const { payment_url, redirect_url } = response;

      if (payment_url) {
        // Отслеживаем запрос ссылки на оплату
        trackAction(GOALS.ACTION_PAYMENT_REQUESTED, {
          tarifId: tarif.id,
          tarifTitle: tarif.title,
          isTrial: tarif.is_trial,
          cost: tarif.cost
        });
        
        window.location.href = payment_url;
      } else {
        navigate(redirect_url || "/more");
      }
    } catch (error) {
      console.error("Ошибка при создании платежа:", error);
    }
  };

  return (
    <div className={`${styles.slide} ${tarif.is_trial ? styles.trialSlide : ''}`}>
      <h3 className={styles.title}>
        {tarif.title}
        {tarif.is_trial && (
          <span className="ml-2 px-2 py-1 bg-green-500 text-white text-xs rounded-full">
            ПРОБНЫЙ
          </span>
        )}
      </h3>
      <p className="text-[14px] text-[#7E7E7E] font-[400] my-0">
        {tarif.is_trial ? `3 дня за 1 ₽` : tarif.term && `${tarif.term} месяца`}
      </p>

      <ul>
        {/* {tarif.features.map((feature, i) => (
          <li key={i} className="flex gap-3 items-center my-4 text-[#292929]">
            <img className="w-[20px]" src={BadgeIcon} alt="BadgeIcon" />
            <span>{feature}</span>
          </li>
        ))} */}
        {baseTarifs.map((feature, i) => (
          <li key={i} className="flex gap-3 items-center my-4 text-[#696969]">
            <img className="w-[20px]" src={IconDone} alt="IconDone" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <h4 className={styles.price}>
        {tarif.is_trial 
          ? `1 ₽ за 3 дня`
          : `${tarif.cost} ₽`
        }
      </h4>
      
      {tarif.is_trial && (
        <div className={styles.trialInfo}>
          <p className={styles.trialPrice}>
            Затем {tarif.cost} ₽ 
          </p>
          <p className={styles.autoRenewal}>
            Автопродление можно отменить в любой момент
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-4">
        <Input
          name="email"
          control={control}
          type="email"
          placeholder="Ваш e-mail"
          rules={{
            required: "Поле обязательно",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Некорректный e-mail",
            },
          }}
        />
        <button
          type="submit"
          className={cn(
            "text-white w-full font-[700] text-[20px] py-[10px] px-[20px] rounded-[36px] transition duration-300 ease-in-out active:bg-[#01BB16]/40",
            tarif.is_trial
              ? "bg-green-500 hover:bg-green-600" 
              : "bg-[#01BB16]"
          )}
        >
          {tarif.is_trial ? "Начать пробный период" : isFreeTariff ? "Получить бесплатно" : "Перейти к оплате"}
        </button>
      </form>
    </div>
  );
};
